import type { RefObject, LegacyRef } from 'react'
import { Clock, RotateCcw } from 'lucide-react'
import clsx from 'clsx'
import type { Message, PendingAction, ToolActivity } from '../types'
import { MessageContent } from './MessageContent'
import { ToolCallCards } from './ToolCallCards'
import { AssistantAvatar } from './AssistantAvatar'
import { ToolActivityBar } from './ToolActivityBar'
import { WriteProposalCard } from './WriteProposalCard'
import { MessageActionBar } from './MessageActionBar'

export interface ChatMessageListProps {
  messages: Message[]
  isTyping: boolean
  streamingAssistantContent: string
  streamStatus: string | null
  toolActivities: ToolActivity[]
  pendingAction: PendingAction | null
  expandedToolCalls: Set<string>
  onToggleToolCall: (messageId: string, callId: string) => void
  onApprovePendingAction: (nonce: string) => void
  onCancelPendingAction: () => void
  onRetry?: () => void
  onFeedback?: (messageId: string, rating: 'up' | 'down' | null) => void
  messagesEndRef: RefObject<HTMLDivElement | null>
  userInitials?: string
}

export function ChatMessageList({
  messages,
  isTyping,
  streamingAssistantContent,
  streamStatus,
  toolActivities,
  pendingAction,
  expandedToolCalls,
  onToggleToolCall,
  onApprovePendingAction,
  onCancelPendingAction,
  onRetry,
  onFeedback,
  messagesEndRef,
  userInitials,
}: ChatMessageListProps) {
  return (
    <div className="flex-1 overflow-y-auto" role="log" aria-label="Conversation messages" aria-busy={isTyping}>
      <div className="max-w-3xl mx-auto">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full py-12">
            <div className="text-center">
              <div className="mb-4 flex justify-center">
                <AssistantAvatar size="lg" surface="light" />
              </div>
              <h2 className="text-xl font-medium text-foreground mb-2">How can I help you today?</h2>
              <p className="text-sm text-muted-foreground">Ask me about test failures, metrics, or automation</p>
            </div>
          </div>
        ) : (
          <div className="px-4 py-8 space-y-6">
            {messages.map((message) => {
              const citableCallIds =
                message.role === 'assistant' && message.toolCalls
                  ? new Set(message.toolCalls.map((r) => r.call_id))
                  : undefined
              return (
                <div
                  key={message.id}
                  className={clsx(
                    'group flex gap-3',
                    message.role === 'user' ? 'justify-end' : 'justify-start',
                  )}
                >
                  {message.role === 'assistant' && (
                    <AssistantAvatar size="sm" surface="brand" />
                  )}
                  <div className="max-w-[72%]">
                    <div
                      className={clsx(
                        'rounded-2xl px-4 py-3',
                        message.role === 'user'
                          ? 'bg-accent/[0.06] text-foreground border border-accent/20'
                          : 'bg-foreground/[0.03] text-foreground',
                      )}
                    >
                      {message.role === 'assistant' ? (
                        <>
                          {message.toolCalls && message.toolCalls.length > 0 && (
                            <ToolCallCards
                              messageId={message.id}
                              records={message.toolCalls}
                              expanded={expandedToolCalls}
                              onToggle={onToggleToolCall}
                            />
                          )}
                          <MessageContent
                            content={message.content}
                            messageId={message.id}
                            citableCallIds={citableCallIds}
                          />
                          {message.isPartial && (
                            <p className="mt-1 text-[10px] text-subtle-foreground italic">Generation stopped</p>
                          )}
                        </>
                      ) : (
                        <div className="text-sm leading-7 whitespace-pre-wrap">
                          {message.content}
                        </div>
                      )}
                    </div>
                    {/* Action bar — copy, feedback (hover-reveal) */}
                    <MessageActionBar
                      content={message.content}
                      role={message.role}
                      messageId={message.id}
                      feedback={message.feedback}
                      onFeedback={message.role === 'assistant' ? onFeedback : undefined}
                    />
                    {/* Retry button for error messages */}
                    {message.isError && onRetry && (
                      <button
                        onClick={onRetry}
                        className="mt-1 flex items-center gap-1 rounded px-2 py-1 text-xs text-muted-foreground transition hover:bg-foreground/[0.10] hover:text-accent"
                      >
                        <RotateCcw className="h-3 w-3" />
                        Retry
                      </button>
                    )}
                  </div>
                  {message.role === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-semibold text-white leading-none">
                        {userInitials || 'U'}
                      </span>
                    </div>
                  )}
                </div>
              )
            })}

            {isTyping && <ToolActivityBar activities={toolActivities} status={streamStatus} />}

            {/* In-progress assistant bubble — fills in token-by-token while the
                stream is running. Replaced by a finalized message in the
                messages array when 'append-message' fires on stream done. */}
            {streamingAssistantContent && (
              <div className="flex gap-3 justify-start" aria-live="polite" aria-atomic="false">
                <AssistantAvatar size="sm" surface="brand" />
                <div className="max-w-[72%] rounded-2xl px-4 py-3 bg-foreground/[0.03] text-foreground">
                  <MessageContent content={streamingAssistantContent} />
                  {isTyping && (
                    <span
                      aria-hidden="true"
                      className="ml-0.5 inline-block animate-pulse text-muted-foreground"
                    >
                      &#x2589;
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Inline approval / write proposal card */}
            {pendingAction && pendingAction.proposal_type === 'write_proposal' ? (
              <WriteProposalCard
                pendingAction={pendingAction}
                onSave={onApprovePendingAction}
                onDiscard={onCancelPendingAction}
              />
            ) : pendingAction ? (
              <div className="flex gap-3 justify-start">
                <div className="w-8" />
                <div className="max-w-[72%] rounded-2xl border border-warning/30 bg-warning-soft px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-warning-foreground" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-warning-foreground">
                      Approval Required
                    </span>
                  </div>
                  <p className="text-sm font-medium text-foreground mb-1">
                    {pendingAction.display?.title || pendingAction.tool_name.replace(/_/g, ' ')}
                  </p>
                  {(pendingAction.display?.details || Object.keys(pendingAction.arguments || {}).length > 0) && (
                    <ul className="text-xs text-muted-foreground mb-3 space-y-0.5">
                      {(
                        pendingAction.display?.details ||
                        Object.entries(pendingAction.arguments || {}).map(([k, v]) => `${k}: ${v}`)
                      )
                        .slice(0, 4)
                        .map((detail, i) => (
                          <li key={i}>
                            &bull; {typeof detail === 'string' ? detail : JSON.stringify(detail)}
                          </li>
                        ))}
                    </ul>
                  )}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onApprovePendingAction(pendingAction.nonce)}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-warning hover:bg-warning/90 rounded-lg transition"
                    >
                      {pendingAction.display?.confirm_label || 'Approve & Run'}
                    </button>
                    <button
                      onClick={onCancelPendingAction}
                      className="px-3 py-1.5 text-xs font-medium text-muted-foreground bg-card border border-subtle hover:bg-foreground/[0.04] rounded-lg transition"
                    >
                      {pendingAction.display?.cancel_label || 'Cancel'}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Typing dots — only shown when stream is active but no content yet */}
            {isTyping && !streamingAssistantContent && (
              <div className="flex gap-3">
                <AssistantAvatar size="sm" surface="brand" />
                <div className="bg-foreground/[0.03] rounded-2xl px-4 py-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-foreground/30 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-foreground/30 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-foreground/30 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef as LegacyRef<HTMLDivElement>} />
          </div>
        )}
      </div>
    </div>
  )
}
